import * as React from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  CircularProgress,
  TextField,
  Button,
  Tabs,
  Tab,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import ReactApexChart from "react-apexcharts";
import { getMonitoring } from "../redux/dynamo_db/actions";

function StatCard({ label, value }) {
  return (
    <Paper
      elevation={2}
      sx={{ p: 2, textAlign: "center", borderTop: "3px solid #6b469e" }}
    >
      <Typography variant="h4" color="primary" fontWeight="bold">
        {value ?? "—"}
      </Typography>
      <Typography variant="body2" color="text.secondary" mt={0.5}>
        {label}
      </Typography>
    </Paper>
  );
}

function SortableTable({ pages }) {
  const [sortField, setSortField] = React.useState("views");
  const [sortDir, setSortDir] = React.useState("desc");

  const handleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const sorted = [...pages].sort((a, b) => {
    const mult = sortDir === "asc" ? 1 : -1;
    return typeof a[sortField] === "number"
      ? mult * (a[sortField] - b[sortField])
      : mult * String(a[sortField]).localeCompare(String(b[sortField]));
  });

  return (
    <Table size="small" sx={{ mt: 2 }}>
      <TableHead>
        <TableRow>
          <TableCell>
            <TableSortLabel
              active={sortField === "pageName"}
              direction={sortField === "pageName" ? sortDir : "asc"}
              onClick={() => handleSort("pageName")}
            >
              Page
            </TableSortLabel>
          </TableCell>
          <TableCell align="right">
            <TableSortLabel
              active={sortField === "views"}
              direction={sortField === "views" ? sortDir : "desc"}
              onClick={() => handleSort("views")}
            >
              Views
            </TableSortLabel>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sorted.length === 0 ? (
          <TableRow>
            <TableCell colSpan={2} align="center">
              <Typography variant="body2" color="text.secondary">
                No data
              </Typography>
            </TableCell>
          </TableRow>
        ) : (
          sorted.map((row) => (
            <TableRow key={row.pageName} hover>
              <TableCell>
                <Typography variant="body2">{row.pageName}</Typography>
              </TableCell>
              <TableCell align="right">{row.views}</TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}

function MonitoringPanel({ data, loading }) {
  if (loading)
    return (
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  if (!data) return null;

  const globalStats = data.global || {};
  const pages = Object.entries(data.pages || {}).map(([pageName, item]) => ({
    pageName,
    views: Number(item.view_count ?? 0),
  }));

  const pieOptions = {
    chart: { type: "pie" },
    labels: pages.map((p) => p.pageName),
    colors: [
      "#6b469e",
      "#9c27b0",
      "#3f51b5",
      "#2196f3",
      "#00bcd4",
      "#009688",
      "#4caf50",
      "#ff9800",
      "#f44336",
      "#e91e63",
    ],
    legend: { position: "bottom" },
  };

  return (
    <>
      <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <StatCard label="Total Page Views" value={globalStats.page_views} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <StatCard label="Sessions" value={globalStats.sessions} />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Typography variant="subtitle1" fontWeight="bold">
            Page Repartition
          </Typography>
          {pages.length > 0 ? (
            <ReactApexChart
              type="pie"
              series={pages.map((p) => p.views)}
              options={pieOptions}
              height={320}
            />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No data
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} md={7}>
          <Typography variant="subtitle1" fontWeight="bold">
            Page Usage
          </Typography>
          <SortableTable pages={pages} />
        </Grid>
      </Grid>
    </>
  );
}

export default function Monitoring() {
  const dispatch = useDispatch();
  const appMonitoring = useSelector((state) => state.appMonitoring);

  const [tab, setTab] = React.useState(0);
  const [month, setMonth] = React.useState(
    new Date().toISOString().slice(0, 7),
  );

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const todayStr = new Date().toISOString().slice(0, 10);
  const yesterdayStr = yesterday.toISOString().slice(0, 10);

  const [startDate, setStartDate] = React.useState(yesterdayStr);
  const [endDate, setEndDate] = React.useState(todayStr);
  const [activeDayKeys, setActiveDayKeys] = React.useState([
    `DAY_${yesterdayStr}`,
  ]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    setLoading(true);
    dispatch(getMonitoring({})); // default: last month + yesterday + lifetime
  }, []);

  React.useEffect(() => {
    if (appMonitoring !== undefined) setLoading(false);
  }, [appMonitoring]);

  const monthKey =
    appMonitoring &&
    Object.keys(appMonitoring).find((k) => k.startsWith("MONTH_"));
  const lifetimeKey =
    appMonitoring && Object.keys(appMonitoring).find((k) => k === "LIFETIME");

  const dayData = React.useMemo(() => {
    if (!appMonitoring) return null;
    const dayEntries = activeDayKeys
      .filter((k) => appMonitoring[k])
      .map((k) => appMonitoring[k]);
    if (dayEntries.length === 0) return null;
    const merged = { global: { page_views: 0, sessions: 0 }, pages: {} };
    for (const d of dayEntries) {
      merged.global.page_views += Number(d.global?.page_views ?? 0);
      merged.global.sessions += Number(d.global?.sessions ?? 0);
      for (const [page, item] of Object.entries(d.pages || {})) {
        merged.pages[page] = {
          view_count:
            Number(merged.pages[page]?.view_count ?? 0) +
            Number(item.view_count ?? 0),
        };
      }
    }
    return merged;
  }, [appMonitoring, activeDayKeys]);

  const loadDays = () => {
    // Generate the expected DAY_ keys for this range
    const start = new Date(startDate);
    const end = new Date(endDate);
    const keys = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      keys.push(`DAY_${d.toISOString().slice(0, 10)}`);
    }
    setActiveDayKeys(keys);
    setLoading(true);
    dispatch(getMonitoring({ start_date: startDate, end_date: endDate }));
  };

  return (
    <React.Fragment>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
        <Tab label="By Month" />
        <Tab label="By Day" />
        <Tab label="Lifetime" />
      </Tabs>

      <div role="tabpanel" hidden={tab !== 0}>
        {tab === 0 && (
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <TextField
                label="Month"
                type="month"
                size="small"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button
                variant="contained"
                onClick={() => {
                  setLoading(true);
                  dispatch(getMonitoring({ month }));
                }}
              >
                Load
              </Button>
            </Box>
            <MonitoringPanel
              data={monthKey ? appMonitoring[monthKey] : null}
              loading={loading}
            />
          </Box>
        )}
      </div>

      <div role="tabpanel" hidden={tab !== 1}>
        {tab === 1 && (
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <TextField
                label="Start Date"
                type="date"
                size="small"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Date"
                type="date"
                size="small"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button variant="contained" onClick={loadDays}>
                Load
              </Button>
            </Box>
            <MonitoringPanel data={dayData} loading={loading} />
          </Box>
        )}
      </div>

      <div role="tabpanel" hidden={tab !== 2}>
        {tab === 2 && (
          <Box sx={{ p: 2 }}>
            <MonitoringPanel
              data={lifetimeKey ? appMonitoring[lifetimeKey] : null}
              loading={loading}
            />
          </Box>
        )}
      </div>
    </React.Fragment>
  );
}
